/* eslint-disable react/jsx-props-no-spreading */
import React, { ChangeEvent, useState } from "react";
import Dropzone, { FileRejection } from "react-dropzone";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import InputSelect from "../../components/InputSelect";
import { StyledFlexEndDiv, StyledLabel } from "../../styles/MainStyles";
import UploadFilesStyles from "../../styles/pages/UploadFilesStyles";
import { ReactComponent as ClipIcon } from "../../assets/icons/Clip.svg";
import { ReactComponent as UploadIcon } from "../../assets/icons/UploadArrowIcon.svg";
import ButtonStyles from "../../styles/components/ButtonStyles";
import { useHelp } from "../../context/HelpContext";
import StringUtils from "../../utils/types/StringUtils";
import HelpService from "../../service/Docente/HelpService";
import { getUserRoleEnumFromNumber } from "../../model/Enums/UserRoleEnum";
import GenericUtils from "../../utils/GenericUtils";
import DialogMessage from "../../components/DialogMessage";
import { DialogConfirm } from "../GestaoFaltas/Components/DialogConfirm";

const { BtnPrimary } = ButtonStyles;
const {
    StyledDropContainer,
    StyledDropzoneError,
    StyledDropzoneParagraph,
    StyledContainerDropzone,
    StyledFileInformationDiv,
    StyledFileName,
    StyledFileSize,
    StyledDropzoneTitle,
    StyledParagraph,
} = UploadFilesStyles;

const HelpUpload = () => {
    const [select, setSelect] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isSuccessUpload, setIsSuccessUpload] = useState<boolean>(false);
    const [newFile, setNewFile] = useState<File[]>();
    const [upload, setUpload] = useState(true);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { instance } = useMsal();
    const { t } = useTranslation();
    const { features, getCreatedHelp, getDocuments } = useHelp();

    const typeFile = "UPLOAD";

    const handleSelectChange = (event: ChangeEvent<{ value: unknown }>) => {
        setSelect(event.target.value as string);
    };

    const acceptFiles = {
        "application/pdf": [".pdf "],
    };

    const handleUpload = (file: File[]) => {
        setNewFile(file);
        setIsError(false);
        setUpload(true);
        setErrorMessage("");
    };

    const handleReject = (file: FileRejection[]) => {
        if (file[0].errors[0].code === "file-invalid-type") {
            setIsError(true);
            setUpload(false);
            setNewFile([]);
        }
    };

    const formatBytes = (bytes: any, decimals = 2) => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
    };

    const convertedSize = formatBytes(newFile?.[0]?.size);

    const dragFileText = "Arraste ou solte seus arquivos aqui";

    const dropzoneDefault = (
        <StyledContainerDropzone>
            <UploadIcon style={{ margin: "40px 0 20px 0" }} />
            <StyledDropzoneTitle>{dragFileText}</StyledDropzoneTitle>
            <BtnPrimary style={{ margin: "20px 0 30px 0" }}>
                Selecionar arquivo
            </BtnPrimary>
            <div>Faça o upload de um arquivo por vez no formato .pdf</div>
        </StyledContainerDropzone>
    );

    const dropzoneWithFile = (
        <StyledContainerDropzone>
            <StyledFileInformationDiv>
                <ClipIcon />
                <StyledFileName>{newFile?.[0]?.name}</StyledFileName>
                <p style={{ margin: 0, color: "#E4E4E4" }}>|</p>
                <StyledFileSize>{convertedSize}</StyledFileSize>
            </StyledFileInformationDiv>
        </StyledContainerDropzone>
    );

    const renderDragMessage = (
        isDragActive: boolean,
        isDragReject: boolean
    ) => {
        if (!isDragActive) {
            return (
                <Container>
                    {newFile && upload ? dropzoneWithFile : dropzoneDefault}
                </Container>
            );
        }

        if (isDragReject) {
            setErrorMessage("");
            setIsError(false);
            return <StyledParagraph>arquivo não suportado</StyledParagraph>;
        }

        setIsError(false);
        setErrorMessage("");
        return <StyledParagraph>SOLTE OS ARQUIVOS AQUI</StyledParagraph>;
    };

    const renderMessageError = (fileRejections: FileRejection[]) => {
        if (fileRejections && isError) {
            return (
                <StyledDropzoneError>
                    <StyledDropzoneParagraph>
                        {StringUtils.capitalizeFirstLetter(
                            t("onlyFilesExtensionsPDF")
                        )}
                    </StyledDropzoneParagraph>
                </StyledDropzoneError>
            );
        }

        return null;
    };

    const uploadHelp = async () => {
        setIsLoading(true);

        const featureId = getUserRoleEnumFromNumber(Number(select));
        const helpId = getCreatedHelp(featureId);

        const response = await HelpService.getFileUrl(
            instance,
            featureId,
            newFile?.[0]?.name,
            typeFile
        );

        if (
            GenericUtils.isSuccessStatus(response) &&
            response.data &&
            response.data?.link.length &&
            response.data?.fileName.length
        ) {
            const responseUpload = await HelpService.uploadFileByUrl(
                response.data?.link,
                newFile?.[0]
            );

            if (GenericUtils.isSuccessStatus(responseUpload)) {
                let responseLink;

                if (helpId === 0) {
                    responseLink = await HelpService.createHelp(
                        instance,
                        featureId,
                        response.data?.fileName
                    );
                } else {
                    responseLink = await HelpService.updateHelp(
                        instance,
                        helpId,
                        featureId,
                        response.data?.fileName
                    );
                }

                if (GenericUtils.isSuccessStatus(responseLink)) {
                    setIsSuccessUpload(true);
                    getDocuments();
                } else {
                    setErrorMessage(responseLink.data?.details);
                }
            } else {
                setErrorMessage(responseUpload.data);
            }
        } else {
            setErrorMessage(response.data?.details);
        }

        setSelect("");
        setErrorMessage("");
        setNewFile([]);
        setUpload(false);
        setIsLoading(false);
    };

    const handleCloseDialogError = () => {
        setErrorMessage("");
    };

    const DialogError = Boolean(errorMessage) && (
        <DialogMessage
            type="error"
            show={Boolean(errorMessage)}
            title={errorMessage}
            onClose={handleCloseDialogError}
        />
    );

    const handleCloseDialogSuccess = () => {
        setIsSuccessUpload(false);
    };

    const DialogSuccess = isSuccessUpload && (
        <DialogConfirm
            openModal={isSuccessUpload}
            handleClose={handleCloseDialogSuccess}
            modalTitle="Upload concluído"
            acceptButtonText="Ok"
        />
    );

    return (
        <>
            <div>
                <div style={{ maxWidth: "735px" }}>
                    <StyledLabel>
                        1. Selecione o tipo de funcionalidade
                    </StyledLabel>
                    <InputSelect
                        value={select}
                        handleSelectChange={handleSelectChange}
                        optionsSelect={features}
                        disabled={isLoading}
                    />
                    <div style={{ height: "290px", marginTop: "35px" }}>
                        <StyledLabel>2. Anexe seu arquivo</StyledLabel>
                        <Dropzone
                            accept={acceptFiles}
                            onDropAccepted={handleUpload}
                            onDropRejected={handleReject}
                            multiple={false}
                            disabled={isLoading}
                        >
                            {({
                                getRootProps,
                                getInputProps,
                                isDragActive,
                                isDragReject,
                                fileRejections,
                            }) => (
                                <StyledDropContainer
                                    {...getRootProps()}
                                    isDragActive={isDragActive}
                                    isDragReject={isDragReject}
                                    style={{ height: "100%" }}
                                >
                                    <input {...getInputProps()} />
                                    {renderDragMessage(
                                        isDragActive,
                                        isDragReject
                                    )}
                                    {renderMessageError(fileRejections)}
                                </StyledDropContainer>
                            )}
                        </Dropzone>
                    </div>
                </div>
                <StyledFlexEndDiv width={735} margin={90}>
                    <BtnPrimary
                        onClick={() => uploadHelp()}
                        disabled={
                            !((newFile?.length || 0) > 0) ||
                            select === "" ||
                            isLoading
                        }
                    >
                        Realizar Upload
                    </BtnPrimary>
                </StyledFlexEndDiv>
            </div>
            {DialogError}
            {DialogSuccess}
        </>
    );
};

export default HelpUpload;
